import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import WhyBlackRidge from "@/components/WhyBlackRidge";
import CTASection from "@/components/CTASection";
import { SITE } from "@/lib/site";
import { Phone } from "lucide-react";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "About" }];

export default function About() {
  return (
    <>
      <SEO title="About Black Ridge Construction | Austin TX General Contractor" description="Learn about Black Ridge Construction — a full-service residential general contractor focused on long-term relationships, clear communication, and quality work across Austin and Central Texas." />

      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">About</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Built for Long-Term Homeowner Relationships</h1>
            <p className="mt-4 text-gray-300">Black Ridge Construction is a residential general contractor serving Austin and Central Texas. We exist to help homeowners protect, improve, and maintain their homes without juggling five different contractors. Roofing, remodeling, repairs, additions, exterior work, and ongoing maintenance — one team, one relationship.</p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-br grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="br-eyebrow">Our Story</div>
            <h2 className="br-headline text-3xl mt-3">Built Around Real Homeowner Problems</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">Most homeowners do not need another single-trade installer. They need someone reliable enough to call about whatever comes up — a leak, a remodel, a repair, a major project, or just keeping the home in good shape over time. Black Ridge Construction was built to be that contractor.</p>
            <p className="mt-3 text-gray-700 leading-relaxed">We coordinate roofing, remodeling, additions, exterior repair, interior finish work, and ongoing maintenance under one general contractor. Our goal is to make working with us feel like working with a real partner — not chasing return calls or wondering who to ask about which problem.</p>
          </div>
          <div>
            <div className="br-eyebrow">Our Values</div>
            <h2 className="br-headline text-3xl mt-3">What We Care About</h2>
            <ul className="mt-3 space-y-3 text-sm text-gray-700">
              <li><strong>Clear communication.</strong> You always know what's happening, when, and what it costs.</li>
              <li><strong>Practical pricing.</strong> Honest estimates and realistic options at multiple price levels.</li>
              <li><strong>Quality workmanship.</strong> The kind of work that holds up so the relationship continues.</li>
              <li><strong>Long-term thinking.</strong> We want to be the contractor you call ten years from now.</li>
              <li><strong>Local accountability.</strong> Austin-area focus, real coordination, real follow-through.</li>
            </ul>
            <div className="mt-5 flex gap-3">
              <Link to="/contact" className="br-btn-dark">Contact Us</Link>
              <a href={`tel:${SITE.phoneE164}`} className="br-btn-secondary" style={{color:"#0a0a0a", borderColor:"#0a0a0a"}}><Phone size={14}/> {SITE.phone}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Why Black Ridge</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">What Sets Us Apart</h2>
          </div>
          <WhyBlackRidge />
        </div>
      </section>

      <CTASection />
    </>
  );
}
